import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  NumberInput,
  Switch,
} from '@commenda/kakashi';
import type { MembershipConfig } from './compute';
import type { OwnershipFieldSettings } from './types';
import { useRevealErrors } from './useRevealErrors';

interface MembershipSectionsProps {
  config: MembershipConfig;
  onPatch: (patch: Partial<MembershipConfig>) => void;
  // ISO-4217 code shown as the money prefix (e.g. "EUR", "USD").
  currency: string;
  // Author-configured, jurisdiction-driven: min capital + helper copy.
  jurisdiction?: { minShareCapital?: number; helperText?: string };
  // Per-field visibility/editability (totalShareCapital, totalUnits).
  fieldSettings: OwnershipFieldSettings;
  // True when at least one member is staged — total capital is then required.
  hasOwners: boolean;

  required: boolean;
  showErrors: boolean;
}

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <h3 className="text-base font-bold text-text-primary">{title}</h3>
      {description ? (
        <p className="text-sm text-text-secondary">{description}</p>
      ) : null}
    </div>
    {children}
  </div>
);

const MoneyPrefix = (code: string) => () => (
  <span className="flex w-full items-center justify-start txt-small-regular text-text-tertiary">
    {code}
  </span>
);

export const MembershipSections = ({
  config,
  onPatch,
  currency,
  jurisdiction,
  fieldSettings,
  hasOwners,
  required,
  showErrors,
}: MembershipSectionsProps) => {
  const capital = fieldSettings.totalShareCapital;
  const units = fieldSettings.totalUnits;
  const minCapital = jurisdiction?.minShareCapital;
  // Each field reveals its error on blur; a submit/next attempt reveals them all.
  const { touch, reveal } = useRevealErrors(showErrors);
  const needFill = required || hasOwners;
  const belowMinCapital =
    minCapital != null &&
    config.totalShareCapital != null &&
    config.totalShareCapital < minCapital &&
    reveal('totalShareCapital');
  const capitalMissing =
    reveal('totalShareCapital') &&
    needFill &&
    !(config.totalShareCapital && config.totalShareCapital >= 1);
  const unitsMissing =
    reveal('totalUnits') &&
    needFill &&
    !!config.hasIssuedUnits &&
    !(config.totalUnits && config.totalUnits >= 1);

  return (
    <div className="flex flex-col gap-8">
      <Section
        title="Ownership Structure"
        description="Define how ownership is structured for your entity and how it's managed."
      >
        {capital.visible ? (
          <FieldWrapper _className="w-1/2">
            <Label
              required={required}
              className="!font-normal text-text-tertiary"
            >
              Total initial capital contribution
            </Label>
            <NumberInput
              formatWithCommas
              className="pl-12"
              placeholder="100,000"
              value={config.totalShareCapital ?? undefined}
              leftEl={MoneyPrefix(currency)}
              error={belowMinCapital || capitalMissing ? ' ' : undefined}
              showErrorMessage={false}
              disabled={!capital.editable}
              onBlur={() => touch('totalShareCapital')}
              onChange={(n) => onPatch({ totalShareCapital: n ?? undefined })}
            />
            {belowMinCapital ? (
              <InputErrorMessage
                message={`Minimum capital is ${currency} ${(minCapital as number).toLocaleString('en-US')}.`}
              />
            ) : capitalMissing ? (
              <InputErrorMessage message="Total initial capital contribution is required to save members." />
            ) : (
              <span className="txt-extra-small-regular text-text-tertiary">
                {jurisdiction?.helperText ??
                  'Sum of member capital contributions must equal this.'}
              </span>
            )}
          </FieldWrapper>
        ) : null}

        <div className="flex w-1/2 items-start gap-3">
          <Switch
            checked={!!config.managerManaged}
            onCheckedChange={(checked) => onPatch({ managerManaged: checked })}
          />
          <div className="flex flex-col">
            <Label className="text-text-primary">Manager-managed</Label>
            <span className="txt-extra-small-regular text-text-tertiary">
              By default, the entity is managed by all its members. Turn this on
              if you want a designated manager to run your entity instead.
            </span>
          </div>
        </div>

        {units.visible ? (
          <div className="flex w-1/2 items-start gap-3">
            <Switch
              checked={!!config.hasIssuedUnits}
              disabled={!units.editable}
              onCheckedChange={(checked) =>
                onPatch({ hasIssuedUnits: checked })
              }
            />
            {/* Label + (when on) the units input share the same left edge, indented past the switch. */}
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex flex-col">
                <Label className="text-text-primary">Issue units</Label>
                <span className="txt-extra-small-regular text-text-tertiary">
                  Most entities use percentages, which keep things simple.
                </span>
              </div>
              {config.hasIssuedUnits ? (
                <FieldWrapper>
                  <Label
                    required={required}
                    className="!font-normal text-text-tertiary"
                  >
                    Total units
                  </Label>
                  <NumberInput
                    formatWithCommas
                    placeholder="1,000"
                    value={config.totalUnits ?? undefined}
                    error={unitsMissing ? ' ' : undefined}
                    showErrorMessage={false}
                    disabled={!units.editable}
                    onBlur={() => touch('totalUnits')}
                    onChange={(n) => onPatch({ totalUnits: n ?? undefined })}
                  />
                  {unitsMissing ? (
                    <InputErrorMessage message="Total units is required when issuing units." />
                  ) : (
                    <span className="txt-extra-small-regular text-text-tertiary">
                      Sum of member units must equal this value.
                    </span>
                  )}
                </FieldWrapper>
              ) : null}
            </div>
          </div>
        ) : null}
      </Section>
    </div>
  );
};
